/**
 * Which teams read as JUCO, rolled up from their players.
 *
 * WHY A ROLLUP AND NOT A LOOKUP. `SchoolDivision` already encodes JUCO (`JUCO`, `JC_CCCAA`,
 * `JC_D1`…), and reading it off the team's school would be the obvious source — but `Team` has no
 * `SchoolId`, no join table links the two, and `School` hangs off `Organization` alone. The
 * division is genuinely unreachable from a team without adding an FK, which is out of scope here.
 * So a team reads as JUCO when its players do.
 *
 * THRESHOLD: A MAJORITY OF AT LEAST THREE PLAYERS WHOSE LEVEL IS KNOWN.
 *
 * Three decisions are packed into that, all deliberate:
 *
 *   - *Majority*, not *any*. A single mis-tagged player must not re-label a whole team. This is a
 *     live risk, not a hypothetical: the scout book Patrick sent carries a prep program (TCS,
 *     p35) inside the JUCO book, and a stray JUCO row on that team's roster would otherwise brand
 *     it as a junior college.
 *   - *Of the players whose level is known*. A null level means "nobody has said yet", not "not
 *     JUCO". Counting nulls as non-JUCO would leave a genuine JUCO team unmarked for as long as
 *     levels are still being backfilled — i.e. for the whole window in which this marker is most
 *     useful.
 *   - *At least three of them.* Without a floor a majority is not a majority at small samples: a
 *     cross-team page that happens to hold one player from a team makes `1 of 1` JUCO, which
 *     clears any majority test and collapses the rule back to "any". Three is the smallest sample
 *     on which "most of them" means anything, and it is small enough that a real JUCO roster
 *     clears it immediately.
 *
 * A team with too few known levels is never marked; the rollup has nothing solid to go on and
 * says so by staying quiet.
 *
 * CALLERS MUST NOT TREAT A `false` AS A VERDICT. "Too few known levels to say" and "looked, not
 * JUCO" are the same `false` here, so anywhere a reading REPLACES a stored one — the mobile tab
 * strip publishes per team — the caller has to withhold the publish for an unrepresentative
 * sample rather than write the `false`. An empty list, a list still loading, a failed fetch and a
 * filtered slice all produce `false`, and publishing any of them un-marks a team that was marked
 * correctly.
 *
 * SCOPE OF THE INPUT. This reads the prospects the caller has already loaded, which on a
 * paginated list is the current page. A team with no player on the page is simply unmarked — the
 * marker is a hint on the rows in front of the coach, not a stored fact about the team. Mirrors
 * `mobile/utils/juco-team-rollup.ts`; keep the threshold identical or the two platforms will
 * disagree about the same team.
 */

import { isJucoPlayerLevel } from "../constants/playerLevels";

/**
 * The smallest sample the rollup will draw a conclusion from.
 *
 * See the threshold note above: below this, a "majority" is one or two players and the rule stops
 * protecting anything. Keep identical across platforms.
 */
export const MIN_KNOWN_LEVELS = 3;

/** The only fields the rollup reads. Both prospect response shapes fit. */
export interface ProspectTeamLevel {
  playerLevel?: number | null;
  teams: { id: string }[];
}

interface TeamTally {
  /** Players on this team whose level is known, JUCO or otherwise. */
  known: number;
  /** Players on this team whose level is JUCO. */
  juco: number;
}

/** True when a tally clears the threshold: a strict majority of at least `MIN_KNOWN_LEVELS`. */
export function isJucoTally({ known, juco }: TeamTally): boolean {
  // `juco * 2 > known` is a strict majority without floating-point division.
  return known >= MIN_KNOWN_LEVELS && juco * 2 > known;
}

/** The ids of every team in `prospects` that reads as JUCO. */
export function jucoTeamIds(
  prospects: readonly ProspectTeamLevel[],
): Set<string> {
  const tallies = new Map<string, TeamTally>();

  for (const prospect of prospects) {
    // `playerLevel == null` contributes to neither count — see the threshold note above.
    if (prospect.playerLevel == null) continue;
    const isJuco = isJucoPlayerLevel(prospect.playerLevel);

    for (const team of prospect.teams ?? []) {
      const tally = tallies.get(team.id) ?? { known: 0, juco: 0 };
      tally.known += 1;
      if (isJuco) tally.juco += 1;
      tallies.set(team.id, tally);
    }
  }

  const jucoIds = new Set<string>();
  for (const [teamId, tally] of tallies) {
    if (isJucoTally(tally)) jucoIds.add(teamId);
  }
  return jucoIds;
}

/**
 * Whether one roster reads as JUCO, ignoring team membership entirely.
 *
 * For a caller that has already scoped its players to a single team — a team's own tab — this is
 * the authoritative reading, and a better one than picking that team out of {@link jucoTeamIds}
 * over a cross-team page: it is the whole roster rather than whichever handful of its players
 * landed on the current page. Same threshold, so the two cannot disagree about the same sample.
 */
export function isJucoRoster(
  prospects: readonly { playerLevel?: number | null }[],
): boolean {
  let known = 0;
  let juco = 0;

  for (const prospect of prospects) {
    if (prospect.playerLevel == null) continue;
    known += 1;
    if (isJucoPlayerLevel(prospect.playerLevel)) juco += 1;
  }

  return isJucoTally({ known, juco });
}
